import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { studyService } from '../services/api';

function StudyBuddyPage() {
    const [sessions, setSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState('');
    const [pastedText, setPastedText] = useState('');
    const [pastedTitle, setPastedTitle] = useState('');
    const [inputMode, setInputMode] = useState('file'); // 'file' or 'text'
    const [uploading, setUploading] = useState(false);
    const [loadingSession, setLoadingSession] = useState(false);
    const [asking, setAsking] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('explanation');
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // Load sessions on mount and auto-load most recent
    useEffect(() => {
        loadSessions();
    }, []);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const loadSessions = async () => {
        try {
            const res = await studyService.getSessions();
            const sessionList = res.data.sessions;
            setSessions(sessionList);
            // auto-load most recent session
            if (sessionList.length > 0) {
                await loadSession(sessionList[0].id);
            }
        } catch (err) {
            console.error('Failed to load sessions');
        }
    };

    const loadSession = async (sessionId) => {
        setLoadingSession(true);
        setError('');
        try {
            const res = await studyService.getSession(sessionId);
            const data = res.data;
            setActiveSession({
                session_id: data.session_id,
                file_name: data.file_name,
                explanation: data.explanation,
                expires_at: data.expires_at,
                created_at: data.created_at,
            });
            setMessages(data.messages || []);
            setActiveTab('explanation');
        } catch (err) {
            setError('Could not load session.');
        } finally {
            setLoadingSession(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('document', file);
        try {
            const response = await studyService.upload(formData);
            const data = response.data;
            const newSession = {
                session_id: data.session_id,
                file_name: data.file_name,
                explanation: data.explanation,
                expires_at: data.expires_at,
                created_at: data.created_at,
            };
            setActiveSession(newSession);
            setMessages([]);
            setActiveTab('explanation');
            setSessions(prev => [{
                id: data.session_id,
                file_name: data.file_name,
                expires_at: data.expires_at,
                created_at: data.created_at,
            }, ...prev]);
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleTextSubmit = async () => {
        if (!pastedText.trim()) return;
        setUploading(true);
        setError('');
        try {
            const response = await studyService.uploadText(
                pastedTitle.trim() || 'My Notes',
                pastedText.trim()
            );
            const data = response.data;
            const newSession = {
                session_id: data.session_id,
                file_name: data.file_name,
                explanation: data.explanation,
                expires_at: data.expires_at,
                created_at: data.created_at,
            };
            setActiveSession(newSession);
            setMessages([]);
            setActiveTab('explanation');
            setSessions(prev => [{
                id: data.session_id,
                file_name: data.file_name,
                expires_at: data.expires_at,
                created_at: data.created_at,
            }, ...prev]);
            setPastedText('');
            setPastedTitle('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleAskQuestion = async () => {
        if (!question.trim() || !activeSession) return;
        const userMsg = { role: 'user', content: question };
        setMessages(prev => [...prev, userMsg]);
        setQuestion('');
        setAsking(true);
        try {
            const response = await studyService.askQuestion(activeSession.session_id, question);
            setMessages(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not get answer. Please try again.');
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setAsking(false);
        }
    };

    const handleDownload = async () => {
        if (!activeSession) return;
        setDownloading(true);
        try {
            const response = await studyService.download(activeSession.session_id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `StudyBuddy_${activeSession.file_name.split('.')[0]}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Download failed. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const formatExpiry = (expiresAt) => {
        if (!expiresAt) return '';
        const diff = new Date(expiresAt) - new Date();
        if (diff <= 0) return 'Expired';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) return `${hours}h ${mins}m left`;
        return `${mins}m left`;
    };

    // Render markdown-like explanation nicely
    const renderExplanation = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => {
            const clean = line
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/`(.*?)`/g, '<code style="background:#f1f5f9;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:0.85em">$1</code>');

            if (line.startsWith('#### ')) {
                return <div key={i} style={{ fontSize: '0.95rem', fontWeight: '700', color: '#374151', marginTop: '0.75rem', marginBottom: '0.25rem' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^#{1,4}\s/, '') }} />;
            }
            if (line.startsWith('### ')) {
                return <div key={i} style={{ fontSize: '1.05rem', fontWeight: '700', color: '#1e293b', marginTop: '1rem', marginBottom: '0.4rem' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^###\s/, '') }} />;
            }
            if (line.startsWith('## ')) {
                return <div key={i} style={{ fontSize: '1.15rem', fontWeight: '700', color: '#1d4ed8', marginTop: '1.5rem', marginBottom: '0.5rem', paddingBottom: '0.25rem', borderBottom: '1px solid #dbeafe' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^##\s/, '') }} />;
            }
            if (line.startsWith('# ')) {
                return <div key={i} style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1e293b', marginTop: '2rem', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e8ecf0' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^#\s/, '') }} />;
            }
            if (line.startsWith('- ') || line.startsWith('• ')) {
                return (
                    <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                        <span style={{ color: '#3b82f6', flexShrink: 0, marginTop: '0.1rem' }}>•</span>
                        <span style={{ color: '#374151', fontSize: '0.92rem', lineHeight: '1.65' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^[-•]\s/, '') }} />
                    </div>
                );
            }
            if (/^\d+\./.test(line)) {
                const num = line.match(/^\d+/)[0];
                return (
                    <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.35rem', paddingLeft: '0.5rem' }}>
                        <span style={{ color: '#3b82f6', flexShrink: 0, fontWeight: '700', minWidth: '1.5rem' }}>{num}.</span>
                        <span style={{ color: '#374151', fontSize: '0.92rem', lineHeight: '1.65' }} dangerouslySetInnerHTML={{ __html: clean.replace(/^\d+\.\s/, '') }} />
                    </div>
                );
            }
            if (!line.trim()) return <div key={i} style={{ height: '0.6rem' }} />;
            return <p key={i} style={{ color: '#374151', fontSize: '0.92rem', lineHeight: '1.7', margin: '0.2rem 0' }} dangerouslySetInnerHTML={{ __html: clean }} />;
        });
    };

    return (
        <Layout>
            <SEO
                title="Study Buddy"
                description="Upload documents and get AI-powered explanations, summaries, and answers. Your intelligent study companion."
                path="/study"
            />
            <div className="page-container">

                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1>📚 Study Buddy</h1>
                        <p className="subtitle">
                            Upload or paste any content — get a detailed AI explanation, then ask questions
                        </p>
                    </div>
                    <div className="study-buddy-header-actions">
                        {activeSession && (
                            <button onClick={handleDownload} disabled={downloading} className="btn-secondary">
                                {downloading ? '⏳ Downloading...' : '📥 Download PDF'}
                            </button>
                        )}
                    </div>
                </div>

                {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

                <div className="study-buddy-layout">

                    {/* Left sidebar */}
                    <div className="study-buddy-sidebar">

                        {/* Upload / Paste card */}
                        <div className="card" style={{ padding: '1rem' }}>
                            <h3 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#64748b', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                New Study Session
                            </h3>

                            {/* Mode toggle */}
                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <button
                                    onClick={() => setInputMode('file')}
                                    style={{
                                        flex: 1, padding: '0.4rem', borderRadius: '6px',
                                        border: inputMode === 'file' ? '2px solid #3b82f6' : '2px solid #e8ecf0',
                                        background: inputMode === 'file' ? '#eff6ff' : 'white',
                                        color: inputMode === 'file' ? '#1d4ed8' : '#64748b',
                                        fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
                                    }}
                                >
                                    📄 Upload File
                                </button>
                                <button
                                    onClick={() => setInputMode('text')}
                                    style={{
                                        flex: 1, padding: '0.4rem', borderRadius: '6px',
                                        border: inputMode === 'text' ? '2px solid #3b82f6' : '2px solid #e8ecf0',
                                        background: inputMode === 'text' ? '#eff6ff' : 'white',
                                        color: inputMode === 'text' ? '#1d4ed8' : '#64748b',
                                        fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
                                    }}
                                >
                                    ✏️ Paste Text
                                </button>
                            </div>

                            {inputMode === 'file' ? (
                                <>
                                    <label
                                        htmlFor="study-file-input"
                                        style={{
                                            display: 'block',
                                            padding: '1rem',
                                            background: '#f8fafc',
                                            border: '2px dashed #cbd5e1',
                                            borderRadius: '8px',
                                            textAlign: 'center',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            opacity: uploading ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>📄</div>
                                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600' }}>
                                            {uploading ? 'Explaining document...' : 'Click to upload'}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                            PDF, DOCX, PPTX, TXT
                                        </div>
                                    </label>
                                    <input
                                        id="study-file-input"
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.docx,.pptx,.txt"
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                        style={{ display: 'none' }}
                                    />
                                </>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        value={pastedTitle}
                                        onChange={e => setPastedTitle(e.target.value)}
                                        placeholder="Title (optional)"
                                        style={{
                                            padding: '0.5rem 0.75rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                        }}
                                    />
                                    <textarea
                                        value={pastedText}
                                        onChange={e => setPastedText(e.target.value)}
                                        placeholder="Paste your text, notes, or content here..."
                                        rows={5}
                                        style={{
                                            padding: '0.5rem 0.75rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            resize: 'vertical',
                                            fontFamily: 'inherit',
                                        }}
                                    />
                                    <button
                                        onClick={handleTextSubmit}
                                        disabled={uploading || !pastedText.trim()}
                                        className="btn-primary"
                                        style={{ width: '100%', opacity: uploading || !pastedText.trim() ? 0.6 : 1 }}
                                    >
                                        {uploading ? '⏳ Explaining...' : '🤖 Explain This'}
                                    </button>
                                </div>
                            )}

                            <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#eff6ff', borderRadius: '6px', fontSize: '0.75rem', color: '#1d4ed8', textAlign: 'center' }}>
                                5 credits to explain • 1 credit per question
                            </div>
                        </div>

                        {/* Active sessions list */}
                        <div className="card" style={{ padding: '1rem' }}>
                            <h3 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#64748b', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Active Sessions
                            </h3>
                            {sessions.length === 0 ? (
                                <p style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', padding: '1rem 0' }}>
                                    No active sessions yet
                                </p>
                            ) : (
                                <>
                                    <div className="study-buddy-session-select-wrap">
                                        <select
                                            className="study-buddy-session-select"
                                            value={activeSession?.session_id || sessions[0]?.id || ''}
                                            onChange={e => loadSession(e.target.value)}
                                        >
                                            {sessions.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {(s.file_name.length > 28 ? s.file_name.substring(0, 28) + '...' : s.file_name)} • {formatExpiry(s.expires_at)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="study-buddy-session-list">
                                        {sessions.map(s => (
                                            <div
                                                key={s.id}
                                                onClick={() => loadSession(s.id)}
                                                style={{
                                                    padding: '0.75rem',
                                                    borderRadius: '8px',
                                                    border: activeSession?.session_id === s.id ? '2px solid #3b82f6' : '1px solid #e8ecf0',
                                                    background: activeSession?.session_id === s.id ? '#eff6ff' : '#f8fafc',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                <strong style={{ display: 'block', fontSize: '0.8rem', color: '#1e293b', marginBottom: '0.2rem', wordBreak: 'break-word' }}>
                                                    📄 {s.file_name.length > 28 ? s.file_name.substring(0, 28) + '...' : s.file_name}
                                                </strong>
                                                <span style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: '600' }}>
                                                    ⏰ {formatExpiry(s.expires_at)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Main content area */}
                    <div className="study-buddy-main">
                        {/* Loading session */}
                        {loadingSession && (
                            <div className="card" style={{ textAlign: 'center', padding: '4rem' }}>
                                <div className="loading-spinner" style={{ margin: '0 auto 1rem' }} />
                                <p style={{ color: '#64748b' }}>Loading your study session...</p>
                            </div>
                        )}

                        {/* Uploading/generating */}
                        {uploading && !loadingSession && (
                            <div className="card" style={{ textAlign: 'center', padding: '4rem' }}>
                                <div className="loading-spinner" style={{ margin: '0 auto 1.5rem' }} />
                                <h2 style={{ marginBottom: '0.5rem' }}>Analysing Your Document...</h2>
                                <p style={{ color: '#64748b' }}>
                                    AI is reading and explaining everything in detail. This may take 30-60 seconds.
                                </p>
                            </div>
                        )}

                        {/* No session yet */}
                        {!activeSession && !uploading && !loadingSession && (
                            <div className="card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📚</div>
                                <h2 style={{ marginBottom: '0.75rem' }}>Start a Study Session</h2>
                                <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto' }}>
                                    Upload a file or paste text on the left to get a detailed AI explanation of any document, lecture, or content.
                                </p>
                            </div>
                        )}

                        {/* Active session */}
                        {activeSession && !uploading && !loadingSession && (
                            <>
                                {/* Session header */}
                                <div className="study-buddy-session-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <span style={{ fontSize: '1.25rem' }}>📄</span>
                                        <div>
                                            <strong style={{ display: 'block', color: '#1e293b', fontSize: '0.9rem' }}>
                                                {activeSession.file_name}
                                            </strong>
                                            <span style={{ color: '#f59e0b', fontSize: '0.75rem', fontWeight: '600' }}>
                                                ⏰ {formatExpiry(activeSession.expires_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="study-buddy-tab-actions">
                                        <button
                                            onClick={() => setActiveTab('explanation')}
                                            style={{
                                                padding: '0.4rem 1rem', borderRadius: '20px',
                                                border: activeTab === 'explanation' ? '2px solid #3b82f6' : '2px solid #e8ecf0',
                                                background: activeTab === 'explanation' ? '#eff6ff' : 'white',
                                                color: activeTab === 'explanation' ? '#1d4ed8' : '#64748b',
                                                fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
                                            }}
                                        >
                                            📖 Explanation
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('chat')}
                                            style={{
                                                padding: '0.4rem 1rem', borderRadius: '20px',
                                                border: activeTab === 'chat' ? '2px solid #3b82f6' : '2px solid #e8ecf0',
                                                background: activeTab === 'chat' ? '#eff6ff' : 'white',
                                                color: activeTab === 'chat' ? '#1d4ed8' : '#64748b',
                                                fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer',
                                            }}
                                        >
                                            💬 Ask Questions {messages.length > 0 && `(${Math.floor(messages.length / 2)})`}
                                        </button>
                                    </div>
                                </div>

                                {/* Explanation tab */}
                                {activeTab === 'explanation' && (
                                    <div className="card">
                                        <div className="study-buddy-explanation-header">
                                            <div>
                                                <h2 style={{ marginBottom: '0.25rem' }}>📖 Detailed Explanation</h2>
                                                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                                                    Full breakdown — every concept explained clearly
                                                </p>
                                            </div>
                                            <div className="study-buddy-explanation-actions">
                                                <button onClick={handleDownload} disabled={downloading} className="btn-secondary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.9rem' }}>
                                                    {downloading ? '⏳' : '📥 PDF'}
                                                </button>
                                                <button onClick={() => setActiveTab('chat')} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
                                                    💬 Ask →
                                                </button>
                                            </div>
                                        </div>
                                        <div style={{ lineHeight: '1.7' }}>
                                            {renderExplanation(activeSession.explanation)}
                                        </div>
                                        <div className="study-buddy-explanation-footer">
                                            <button onClick={handleDownload} disabled={downloading} className="btn-primary">
                                                {downloading ? '⏳ Downloading...' : '📥 Download PDF Summary'}
                                            </button>
                                            <button onClick={() => setActiveTab('chat')} className="btn-secondary">
                                                💬 Ask a Question
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Chat tab */}
                                {activeTab === 'chat' && (
                                    <div className="card study-buddy-chat-card">
                                        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e8ecf0' }}>
                                            <h2 style={{ marginBottom: '0.25rem' }}>💬 Ask Questions</h2>
                                            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                                                Ask anything about your document — 1 credit per question
                                            </p>
                                        </div>

                                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.5rem 0', marginBottom: '1rem' }}>
                                            {messages.length === 0 && (
                                                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💬</div>
                                                    <p style={{ marginBottom: '1rem' }}>Ask any question about your document!</p>
                                                    <div className="study-buddy-chat-suggestions">
                                                        {[
                                                            'Can you summarize the main points?',
                                                            'Explain this in simpler terms',
                                                            'What are the key concepts?',
                                                            'Give me an example',
                                                            'What should I focus on?',
                                                        ].map((s, i) => (
                                                            <button key={i} onClick={() => setQuestion(s)} style={{ background: '#f8fafc', border: '1px solid #e8ecf0', borderRadius: '20px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer' }}>
                                                                {s}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {messages.map((msg, i) => (
                                                <div key={i} className={`study-buddy-chat-row ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                                                    {msg.role === 'assistant' && (
                                                        <div style={{ width: '32px', height: '32px', background: '#3b82f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>📚</div>
                                                    )}
                                                    <div
                                                        className={`study-buddy-chat-bubble ${msg.role === 'user' ? 'user' : 'assistant'}`}
                                                    >
                                                        {msg.content}
                                                    </div>
                                                    {msg.role === 'user' && (
                                                        <div style={{ width: '32px', height: '32px', background: '#1e293b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'white', fontWeight: '700', flexShrink: 0 }}>You</div>
                                                    )}
                                                </div>
                                            ))}

                                            {asking && (
                                                <div className="study-buddy-chat-row assistant">
                                                    <div style={{ width: '32px', height: '32px', background: '#3b82f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>📚</div>
                                                    <div className="study-buddy-chat-bubble assistant" style={{ color: '#64748b' }}>
                                                        Thinking...
                                                    </div>
                                                </div>
                                            )}
                                            <div ref={chatEndRef} />
                                        </div>

                                        <div className="study-buddy-chat-input-row">
                                            <input
                                                type="text"
                                                value={question}
                                                onChange={e => setQuestion(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAskQuestion()}
                                                placeholder="Ask a question about your document..."
                                                disabled={asking}
                                                className="study-buddy-chat-input"
                                                style={{ padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none' }}
                                            />
                                            <button
                                                onClick={handleAskQuestion}
                                                disabled={asking || !question.trim()}
                                                className="study-buddy-chat-send-btn"
                                                style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: '600', cursor: asking || !question.trim() ? 'not-allowed' : 'pointer', opacity: asking || !question.trim() ? 0.6 : 1 }}
                                            >
                                                {asking ? '⏳' : 'Ask →'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default StudyBuddyPage;
